import { Head, useForm, usePage } from "@inertiajs/react";
import { Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import SectionHeading from "@/components/marketing/SectionHeading";
import FormField from "@/components/forms/FormField";
import SelectField from "@/components/forms/SelectField";
import { Textarea } from "@/components/ui/textarea";
import FormSection from "@/components/forms/FormSection";
import PrivacyConsentCheckbox from "@/components/forms/PrivacyConsentCheckbox";
import Highlight from "@/components/home/Highlight";

export default function New({ provinces = [], payment_processors = [], errors: serverErrors = {}, values = {} }) {
  const { routes } = usePage().props;

  const { data, setData, post, processing, errors: inertiaErrors } = useForm({
    school_name: values.school_name || "",
    school_address: values.school_address || "",
    city: values.city || "",
    province: values.province || "",
    postal_code: values.postal_code || "",
    applicant_name: values.applicant_name || "",
    applicant_email: values.applicant_email || "",
    applicant_role: values.applicant_role || "",
    estimated_students: values.estimated_students || "",
    campaign_start_on: values.campaign_start_on || "",
    campaign_end_on: values.campaign_end_on || "",
    payment_processor: values.payment_processor || "",
    heard_about_us: values.heard_about_us || "",
    notes: values.notes || "",
    privacy_consent: values.privacy_consent === true || values.privacy_consent === "true",
    // Honeypot fields — hidden from humans, filled by bots. Names are
    // deliberately opaque (not "company"/"website"/"email") so browser
    // address/contact autofill can't match and silently fill them, which
    // would drop a legitimate application.
    xq_ref_a: "",
    xq_ref_b: "",
    xq_ref_c: "",
  });

  // Merge server-side validation errors (from Rails redirect) with inertia-native ones.
  const errors = { ...serverErrors, ...inertiaErrors };
  const firstError = (key) => (Array.isArray(errors[key]) ? errors[key][0] : errors[key]);

  const submit = (e) => {
    e.preventDefault();
    post(routes.school_applications);
  };

  return (
    <MarketingLayout>
      <Head title="Sign up your school" />

      {/* ── Hero strip ──────────────────────────────────────── */}
      <section className="section-py border-b border-border/60 bg-card/30">
        <div className="container max-w-3xl">
          <SectionHeading
            as="h1"
            title={<>Sign up your <Highlight color="amber">school</Highlight> for PagePledge</>}
            subhead="Start a free trial: create your school’s account and set up your read-a-thon right away. Running a campaign costs a flat fee, paid before it goes live. Early adopter discounts are available."
            align="left"
            className="mb-0"
          />
        </div>
      </section>

      {/* ── Form ────────────────────────────────────────────── */}
      <section className="section-py">
        <div className="container max-w-3xl">
          <Card>
            <CardHeader>
              <CardTitle>Your school details</CardTitle>
              <CardDescription>
                All fields marked <span className="text-destructive">*</span> are required. We'll use your contact info to help you get set up.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-10">
                <FormSection title="School" description="The school that will run the campaign.">
                  <FormField
                    name="school_name"
                    label="School name"
                    required
                    placeholder="Lincoln Elementary"
                    maxLength={120}
                    value={data.school_name}
                    onChange={(e) => setData("school_name", e.target.value)}
                    error={firstError("school_name")}
                  />
                  <FormField
                    name="school_address"
                    label="Street address"
                    placeholder="1234 Reading Lane"
                    value={data.school_address}
                    onChange={(e) => setData("school_address", e.target.value)}
                    error={firstError("school_address")}
                  />
                  <div className="grid gap-5 md:grid-cols-3">
                    <FormField
                      name="city"
                      label="City"
                      placeholder="Surrey"
                      value={data.city}
                      onChange={(e) => setData("city", e.target.value)}
                      error={firstError("city")}
                    />
                    <SelectField
                      name="province"
                      label="Province"
                      required
                      placeholder="Select…"
                      options={provinces}
                      value={data.province}
                      onChange={(e) => setData("province", e.target.value)}
                      error={firstError("province")}
                    />
                    <FormField
                      name="postal_code"
                      label="Postal code"
                      placeholder="V3S 1A1"
                      value={data.postal_code}
                      onChange={(e) => setData("postal_code", e.target.value)}
                      error={firstError("postal_code")}
                    />
                  </div>
                </FormSection>

                <FormSection title="You" description="We'll send your sign-in link to this email. Choose one main contact for your school.">
                  <div className="grid gap-5 md:grid-cols-2">
                    <FormField
                      name="applicant_name"
                      label="Your name"
                      required
                      placeholder="Morgan Chen"
                      maxLength={120}
                      value={data.applicant_name}
                      onChange={(e) => setData("applicant_name", e.target.value)}
                      autoComplete="name"
                      error={firstError("applicant_name")}
                    />
                    <FormField
                      name="applicant_email"
                      label="Email"
                      type="email"
                      required
                      placeholder="principal@school.ca"
                      value={data.applicant_email}
                      onChange={(e) => setData("applicant_email", e.target.value)}
                      autoComplete="email"
                      error={firstError("applicant_email")}
                    />
                  </div>
                  <FormField
                    name="applicant_role"
                    label="Your role"
                    placeholder="Principal, PAC Chair, Fundraising Committee, etc."
                    value={data.applicant_role}
                    onChange={(e) => setData("applicant_role", e.target.value)}
                    error={firstError("applicant_role")}
                  />
                </FormSection>

                <FormSection title="Campaign" description="A rough sense of what you're planning.">
                  <FormField
                    name="estimated_students"
                    label="Approximately how many students?"
                    type="number"
                    min="1"
                    placeholder="250"
                    value={data.estimated_students}
                    onChange={(e) => setData("estimated_students", e.target.value)}
                    error={firstError("estimated_students")}
                    help="An estimate is fine. You can change it later."
                  />
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">Have dates in mind? Share them below, or leave them blank if you're still deciding.</p>
                    <div className="grid gap-5 md:grid-cols-2">
                      <FormField
                        name="campaign_start_on"
                        label="Campaign start date (optional)"
                        type="date"
                        value={data.campaign_start_on}
                        onChange={(e) => setData("campaign_start_on", e.target.value)}
                        error={firstError("campaign_start_on")}
                      />
                      <FormField
                        name="campaign_end_on"
                        label="Campaign end date (optional)"
                        type="date"
                        min={data.campaign_start_on || undefined}
                        value={data.campaign_end_on}
                        onChange={(e) => setData("campaign_end_on", e.target.value)}
                        error={firstError("campaign_end_on")}
                      />
                    </div>
                  </div>
                  <fieldset className="space-y-3" aria-describedby={firstError("payment_processor") ? "payment_processor_error" : undefined}>
                    <legend className="text-sm font-medium">Does your school already have a payment processing account? <span className="text-destructive">*</span></legend>
                    {payment_processors.map(({ value, label }) => (
                      <label key={value} className="flex cursor-pointer items-center gap-3 rounded-lg border border-input p-3 text-sm has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                        <input
                          type="radio"
                          name="payment_processor"
                          value={value}
                          checked={data.payment_processor === value}
                          onChange={(e) => setData("payment_processor", e.target.value)}
                          required
                          aria-invalid={!!firstError("payment_processor")}
                          className="h-4 w-4 accent-primary"
                        />
                        {label}
                      </label>
                    ))}
                    {firstError("payment_processor") && <p id="payment_processor_error" className="text-sm text-destructive">{firstError("payment_processor")}</p>}
                  </fieldset>
                  <FormField
                    name="heard_about_us"
                    label="How did you hear about us?"
                    placeholder="Another principal, Twitter, etc. (optional)"
                    value={data.heard_about_us}
                    onChange={(e) => setData("heard_about_us", e.target.value)}
                    error={firstError("heard_about_us")}
                  />
                  <FormField
                    name="notes"
                    label="Anything we should know?"
                    error={firstError("notes")}
                  >
                    <Textarea
                      id="notes"
                      name="notes"
                      rows={4}
                      value={data.notes}
                      onChange={(e) => setData("notes", e.target.value)}
                      placeholder="Tell us about your goals, timing, or any questions you have."
                    />
                  </FormField>
                </FormSection>

                {/* ── Honeypot fields (hidden from humans) ── */}
                <div aria-hidden="true" className="sr-only" tabIndex={-1}>
                  <label>
                    Leave this field blank
                    <input
                      type="text"
                      name="xq_ref_a"
                      value={data.xq_ref_a}
                      onChange={(e) => setData("xq_ref_a", e.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </label>
                  <label>
                    Leave this field blank
                    <input
                      type="text"
                      name="xq_ref_b"
                      value={data.xq_ref_b}
                      onChange={(e) => setData("xq_ref_b", e.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </label>
                </div>

                <div className="space-y-4 pt-4 border-t border-border">
                  <PrivacyConsentCheckbox
                    checked={data.privacy_consent}
                    onChange={(checked) => setData("privacy_consent", checked)}
                    error={firstError("privacy_consent")}
                    id="school_application_privacy_consent"
                  />
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <Button type="submit" size="lg" disabled={processing}>
                      <Send className="h-4 w-4" />
                      {processing ? "Submitting…" : "Sign up your school"}
                    </Button>
                    <p className="text-sm text-muted-foreground">
                      By submitting you also agree to our <a href="/terms" target="_blank" rel="noreferrer" className="text-primary hover:underline">Terms of Service</a>.
                    </p>
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </MarketingLayout>
  );
}
